import { useState, useMemo } from 'react';

export interface TimeSlot {
  dayOfWeek: number; // 0=Sunday, 1=Monday, etc.
  startHour: number; // 6-21 (6am-9pm)
}

export interface AvailabilityPickerProps {
  value: TimeSlot[];
  onChange: (slots: TimeSlot[]) => void;
  maxDays?: number; // Default 5 - max days they can select
  minDays?: number; // Default 2 - min days required
  maxSlots?: number; // Default 5 - max total slots allowed
  hourRange?: { start: number; end: number }; // Default 6-21
  disabled?: boolean;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function AvailabilityPicker({
  value,
  onChange,
  maxDays = 5,
  minDays = 2,
  maxSlots = 5,
  hourRange = { start: 6, end: 21 },
  disabled = false,
}: AvailabilityPickerProps) {
  // Track which days are active (toggled on)
  const [activeDays, setActiveDays] = useState<Set<number>>(() => {
    const days = new Set<number>();
    value.forEach((slot) => days.add(slot.dayOfWeek));
    return days;
  });

  // Generate hour list from range
  const hours = useMemo(() => {
    const result = [];
    for (let h = hourRange.start; h <= hourRange.end; h++) {
      result.push(h);
    }
    return result;
  }, [hourRange.start, hourRange.end]);

  // Format hour for display
  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  // Check if a specific time slot is selected
  const isSlotSelected = (dayOfWeek: number, startHour: number): boolean => {
    return value.some(
      (slot) => slot.dayOfWeek === dayOfWeek && slot.startHour === startHour
    );
  };

  // Toggle a day on/off
  const toggleDay = (dayOfWeek: number) => {
    if (disabled) return;

    const newActiveDays = new Set(activeDays);

    if (newActiveDays.has(dayOfWeek)) {
      // Remove day and all its time slots
      newActiveDays.delete(dayOfWeek);
      const newSlots = value.filter((slot) => slot.dayOfWeek !== dayOfWeek);
      onChange(newSlots);
      setActiveDays(newActiveDays);
    } else {
      // Check if we can add more days
      if (newActiveDays.size >= maxDays) {
        return; // Already at max
      }
      newActiveDays.add(dayOfWeek);
      setActiveDays(newActiveDays);
    }
  };

  // Toggle a specific time slot (only one slot per day allowed)
  const toggleSlot = (dayOfWeek: number, startHour: number) => {
    if (disabled) return;

    // Check if this exact slot is already selected
    const existingSlotIndex = value.findIndex(
      (slot) => slot.dayOfWeek === dayOfWeek && slot.startHour === startHour
    );

    // If clicking the same slot, remove it
    if (existingSlotIndex >= 0) {
      const newSlots = value.filter((_, i) => i !== existingSlotIndex);
      // If no more slots on this day, deactivate the day
      if (!newSlots.some((slot) => slot.dayOfWeek === dayOfWeek)) {
        const newActiveDays = new Set(activeDays);
        newActiveDays.delete(dayOfWeek);
        setActiveDays(newActiveDays);
      }
      onChange(newSlots);
      return;
    }

    // Check if there's already a slot on this day (replace it)
    const existingDaySlotIndex = value.findIndex(
      (slot) => slot.dayOfWeek === dayOfWeek
    );

    if (existingDaySlotIndex >= 0) {
      // Replace the existing slot on this day with the new time
      const newSlots = value.map((slot, i) =>
        i === existingDaySlotIndex ? { dayOfWeek, startHour } : slot
      );
      onChange(newSlots);
      return;
    }

    // Adding a new slot on a new day
    // Check if we can add more days
    if (activeDays.size >= maxDays) {
      return; // Already at max days
    }

    // Check if we can add more total slots
    if (value.length >= maxSlots) {
      return; // Already at max slots
    }

    // Activate the day and add the slot
    const newActiveDays = new Set(activeDays);
    newActiveDays.add(dayOfWeek);
    setActiveDays(newActiveDays);

    const newSlots = [...value, { dayOfWeek, startHour }];
    onChange(newSlots);
  };

  // Calculate selected days and slots count
  const selectedDaysCount = activeDays.size;
  const selectedSlotsCount = value.length;
  const meetsMinimum = selectedDaysCount >= minDays;
  const atMaxSlots = selectedSlotsCount >= maxSlots;

  return (
    <div className="space-y-4">
      {/* Header with slot count */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          Select your availability
        </div>
        <div
          className={`text-sm font-medium transition-colors ${
            atMaxSlots
              ? 'text-amber-600 dark:text-amber-400'
              : meetsMinimum
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-gray-500 dark:text-slate-400'
          }`}
        >
          {selectedSlotsCount}/{maxSlots} sessions
        </div>
      </div>

      {/* Grid container */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5">
        <div className="min-w-max">
          {/* Day headers */}
          <div className="grid grid-cols-[auto_repeat(7,1fr)] gap-0 border-b border-gray-200 dark:border-white/10">
            {/* Empty corner cell */}
            <div className="w-16 h-12 border-r border-gray-200 dark:border-white/10" />

            {/* Day buttons */}
            {DAY_LABELS.map((label, dayOfWeek) => {
              const isActive = activeDays.has(dayOfWeek);
              const canActivate = !isActive && selectedDaysCount < maxDays;

              return (
                <button
                  key={dayOfWeek}
                  onClick={() => toggleDay(dayOfWeek)}
                  disabled={disabled || (!isActive && !canActivate)}
                  className={`
                    h-12 min-w-[44px] px-2 font-semibold text-sm transition-all
                    touch-manipulation select-none
                    ${
                      isActive
                        ? 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700'
                        : canActivate
                        ? 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10 active:bg-gray-200 dark:active:bg-white/20'
                        : 'text-gray-400 dark:text-slate-600 cursor-not-allowed'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  aria-label={`${DAY_NAMES[dayOfWeek]}, ${
                    isActive ? 'active, click to deactivate' : 'inactive, click to activate'
                  }`}
                  title={DAY_NAMES[dayOfWeek]}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Time slots grid */}
          {hours.map((hour) => (
            <div
              key={hour}
              className="grid grid-cols-[auto_repeat(7,1fr)] gap-0 border-b border-gray-200 dark:border-white/10 last:border-b-0"
            >
              {/* Hour label */}
              <div className="w-16 h-11 flex items-center justify-center border-r border-gray-200 dark:border-white/10 text-xs font-medium text-gray-600 dark:text-slate-400">
                {formatHour(hour)}
              </div>

              {/* Time slot cells */}
              {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
                const isActive = activeDays.has(dayOfWeek);
                const isSelected = isSlotSelected(dayOfWeek, hour);
                const canActivate = !isActive && activeDays.size < maxDays;
                const isInteractive = !disabled && (isActive || canActivate);

                return (
                  <button
                    key={dayOfWeek}
                    onClick={() => toggleSlot(dayOfWeek, hour)}
                    disabled={!isInteractive}
                    className={`
                      h-11 min-w-[44px] transition-all touch-manipulation
                      ${
                        isSelected
                          ? 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700'
                          : isInteractive
                          ? 'hover:bg-gray-100 dark:hover:bg-white/10 active:bg-gray-200 dark:active:bg-white/20'
                          : 'bg-gray-50 dark:bg-transparent'
                      }
                      ${
                        isInteractive
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed opacity-40'
                      }
                    `}
                    aria-label={`${DAY_NAMES[dayOfWeek]} at ${formatHour(hour)}, ${
                      isSelected ? 'selected' : 'not selected'
                    }`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Validation message */}
      {!meetsMinimum && selectedDaysCount > 0 && (
        <div className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>Please select at least {minDays} days per week</span>
        </div>
      )}

      {/* Instructions */}
      <div className="text-xs text-gray-500 dark:text-slate-500">
        Tap any time block to select your available hours (day auto-activates)
      </div>
    </div>
  );
}
